<template>
  <div>
    <div v-if="isStandalone">
      <div class="form-title title pl-4 py-1">
        <b>{{ windowTitle }}</b>
      </div>
      <div class="form-view">
        <v-card class="drivers-occupancy-window__card">
          <DriversOccupancyList v-if="isDriversListStep" />
          <DriversOccupancyCourseSelection v-else-if="isCourseSelectionStep" />
          <DriversOccupancySuggestedPickup v-else-if="isSuggestedPickupStep" />
          <DriversOccupancySuggestedLeave v-else />
        </v-card>
      </div>
    </div>
    <hsc-window-style-white v-else>
      <hsc-window
        ref="occupancyWindow"
        title="Lista kierowców"
        :position-hint="initialWindowPosition"
        :close-button="true"
        :is-open.sync="showOccupancy"
        class="floating-popup"
        :class="{ 'floating-popup--minimized': isMinimized }"
        resizable
        is-scrollable
        :width="initialWindowWidth"
        :height="initialWindowHeight"
        :min-width="720"
        :min-height="280"
        :style="`z-index: ${dialogZIndex}`"
        @move-start="updateDialogIndex"
        @resize-start="updateDialogIndex"
        @resize="onWindowResize"
      >
        <template #title>
          <div class="d-flex justify-space-between align-center w-100">
            <span>{{ windowTitle }}</span>
            <div class="d-flex align-center">
              <v-btn icon max-height="32" max-width="32" class="mr-2" @click="toggleMinimize">
                <v-icon color="#000">
                  {{ isMinimized ? 'mdi-window-maximize' : 'mdi-window-minimize' }}
                </v-icon>
              </v-btn>
              <v-btn v-if="!isMinimized" icon max-height="32" max-width="32" class="mr-2" @click="openInNewWindow">
                <v-icon color="#000">
                  mdi-window-restore
                </v-icon>
              </v-btn>
            </div>
          </div>
        </template>
        <v-card class="drivers-occupancy-window__card drivers-occupancy-window__card--dialog">
          <DriversOccupancyList v-if="isDriversListStep" :content-height="contentHeight" />
          <DriversOccupancyCourseSelection v-else-if="isCourseSelectionStep" />
          <DriversOccupancySuggestedPickup v-else-if="isSuggestedPickupStep" :content-height="contentHeight" />
          <DriversOccupancySuggestedLeave v-else :content-height="contentHeight" />
        </v-card>
      </hsc-window>
    </hsc-window-style-white>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DriversOccupancyList from './DriversOccupancyList'
import DriversOccupancySuggestedLeave from './DriversOccupancySuggestedLeave'
import DriversOccupancySuggestedPickup from './DriversOccupancySuggestedPickup'
import DriversOccupancyCourseSelection from './DriversOccupancyCourseSelection'
import webSocketMixin from '../../mixins/webSocketMixin'
import { DRIVERS_OCCUPANCY_VIEW } from '../../store/driversOccupancy'
import { openNewWindow } from '../../utils'

const TITLEBAR_HEIGHT = 56
const MINIMIZED_WINDOW_WIDTH = 360
const MINIMIZED_WINDOW_MARGIN = 16

export default {
  components: {
    DriversOccupancyList,
    DriversOccupancySuggestedLeave,
    DriversOccupancySuggestedPickup,
    DriversOccupancyCourseSelection
  },
  mixins: [webSocketMixin],
  channels: {
    DriversDistancesChannel: {
      received(data) {
        this.captureChanges(data, 'driversOccupancy', true)
      }
    }
  },
  data() {
    const initialWindowWidth = Math.min(1400, Math.max(720, window.innerWidth - 80))
    const initialWindowHeight = Math.min(360, Math.max(280, window.innerHeight - 160))

    return {
      tableName: 'driversOccupancy',
      dialogZIndex: 10,
      initialWindowWidth,
      initialWindowHeight,
      contentHeight: initialWindowHeight,
      isMinimized: false,
      restoredWindowStyle: null
    }
  },
  computed: {
    ...mapState({
      isOccupancyDialogOpen: state => state.driversOccupancy.isOccupancyDialogOpen,
      viewStep: state => state.driversOccupancy.viewStep,
      selectedDriver: state => state.driversOccupancy.selectedDriver
    }),
    isDriversListStep() {
      return this.viewStep === DRIVERS_OCCUPANCY_VIEW.LIST
    },
    isSuggestedPickupStep() {
      return this.viewStep === DRIVERS_OCCUPANCY_VIEW.SUGGESTED_PICKUP
    },
    isCourseSelectionStep() {
      return this.viewStep === DRIVERS_OCCUPANCY_VIEW.COURSE_SELECTION
    },
    isStandalone() {
      return this.$route.name === 'driversOccupancy'
    },
    initialWindowPosition() {
      // hsc-window centers by natural content size, which the wide table blows up, so position it explicitly
      const left = Math.max(0, Math.round((window.innerWidth - this.initialWindowWidth) / 2))
      const top = Math.max(0, Math.round((window.innerHeight - this.initialWindowHeight - TITLEBAR_HEIGHT) / 2))

      return `${left}/${top}`
    },
    windowTitle() {
      if (this.isDriversListStep) {
        return 'Lista kierowców'
      }

      const driverName =
        this.selectedDriver?.name ||
        `${this.selectedDriver?.firstName || ''} ${this.selectedDriver?.lastName || ''}`.trim()

      if (this.isCourseSelectionStep) {
        return driverName ? `Wyślij kursy – ${driverName}` : 'Wyślij kursy'
      }

      if (this.isSuggestedPickupStep) {
        return driverName ? `Sugerowane kursy zabrania – ${driverName}` : 'Sugerowane kursy zabrania'
      }

      return driverName ? `Sugerowane kursy podstawienia – ${driverName}` : 'Sugerowane kursy podstawienia'
    },
    showOccupancy: {
      get() {
        return this.isOccupancyDialogOpen
      },
      set(isShown) {
        this.setOccupancyVisibility(isShown)
        this.updateDialogIndex()
      }
    }
  },
  watch: {
    isOccupancyDialogOpen(isOpen) {
      if (isOpen) {
        this.subscribeSocket('DriversDistancesChannel')
      } else {
        this.unsubscribeSocket('DriversDistancesChannel')
        this.restoreWindowSize()
      }
    }
  },
  mounted() {
    if (this.isStandalone) {
      this.setOccupancyVisibility(true)
    }
  },
  beforeDestroy() {
    this.unsubscribeSocket('DriversDistancesChannel')
    if (this.isStandalone) {
      this.setOccupancyVisibility(false)
    }
  },
  methods: {
    ...mapActions({
      setOccupancyVisibility: 'driversOccupancy/setOccupancyVisibility',
      updateDialogIndex: function(dispatch) {
        this.dialogZIndex = this.$store.state.layout.dialogTopIndex
        return dispatch('layout/updateDialogIndex')
      }
    }),
    openInNewWindow() {
      openNewWindow.call(this, { name: 'driversOccupancy' })
    },
    onWindowResize({ height }) {
      if (this.isMinimized) return

      this.contentHeight = height
    },
    windowElements() {
      const windowElement = this.$refs.occupancyWindow?.$el
      const contentElement = windowElement?.querySelector('.content')

      return { windowElement, contentElement }
    },
    toggleMinimize() {
      if (this.isMinimized) {
        this.restoreWindowSize()
        return
      }

      const { windowElement, contentElement } = this.windowElements()
      if (!windowElement || !contentElement) return

      const { width, height, left, top } = windowElement.style
      this.restoredWindowStyle = { width, height, left, top }

      contentElement.style.display = 'none'
      windowElement.style.height = 'auto'
      windowElement.style.width = `${MINIMIZED_WINDOW_WIDTH}px`
      windowElement.style.left = `${MINIMIZED_WINDOW_MARGIN}px`
      windowElement.style.top = `${window.innerHeight - windowElement.offsetHeight - MINIMIZED_WINDOW_MARGIN}px`

      this.isMinimized = true
    },
    restoreWindowSize() {
      if (!this.isMinimized) return

      const { windowElement, contentElement } = this.windowElements()
      this.isMinimized = false

      if (!windowElement || !contentElement) return

      contentElement.style.display = ''
      Object.assign(windowElement.style, this.restoredWindowStyle)
      this.restoredWindowStyle = null
    }
  }
}
</script>

<style lang="scss" scoped>
.form-title {
  background-color: #95a2b710;
  height: 40px;
}

.form-view {
  height: calc(100vh - 40px);
  overflow: auto;
}

.drivers-occupancy-window__card {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: none;

  > * {
    flex: 1;
    min-height: 0;
    height: 100%;
  }

  // keeps the table's scrollbars clear of the dialog's bottom and right resize handles
  &--dialog {
    padding-right: 12px;
    padding-bottom: 12px;
  }
}
</style>
